import { handCategory } from "./evaluator";

/*
 * OPPONENT MODEL — live, public-information-only statistics for every player at the table.
 *
 * The engine feeds this module the same events a human sitting at the table would see:
 * each action (who, street, position, bet size vs pot), street changes, and the cards that
 * are actually turned over at showdown. It never sees folded or unrevealed hole cards.
 *
 * Every stat is an (hits, opportunities) pair with exponential decay (MODEL_CFG.decay), so
 * recent hands weigh more than old ones (effective window ≈ 1 / (1 - decay) opportunities).
 * Estimates are Bayesian-smoothed toward a population prior (PRIORS: [mean, pseudo-count]),
 * so a few early hands cannot swing a read wildly. Multiway observations count less (MULTIWAY_W)
 * because folding more often in a multiway pot is correct and not a leak.
 *
 * Besides long-run stats, the model keeps a per-hand estimate of every player's range
 * (top fraction of starting hands), narrowed by each action using that player's own tendencies.
 * The decision engines read profile() and handRange(). State is plain data so the game can be
 * cloned with structuredClone.
 */

export const MODEL_CFG = { decay: 0.97, confHands: 25 };
const MULTIWAY_W = 0.6;
const STEAL_POS = new Set(["CO", "BTN", "SB"]);
// How much wider or tighter than their average open a player opens from each seat
const POS_OPEN_MULT = { EP: 0.55, MP: 0.75, CO: 1.05, BTN: 1.5, SB: 1.3, BB: 1 };

// [prior mean, prior weight in pseudo-observations] (roughly a solid regular)
export const PRIORS = {
  vpip: [0.26, 8], pfr: [0.18, 8], openRaise: [0.3, 6], limp: [0.08, 6], jam: [0.06, 6], jamAny: [0.05, 6],
  threeBet: [0.07, 10], foldTo3bet: [0.55, 6], steal: [0.38, 6], foldToSteal: [0.62, 6],
  aggFreq: [0.38, 8], cbet: [0.62, 5],
  foldToCbet_flop: [0.42, 6], foldToCbet_turn: [0.45, 5], foldToCbet_river: [0.5, 5],
  foldToBet_flop: [0.42, 6], foldToBet_turn: [0.45, 6], foldToBet_river: [0.5, 6],
  foldToRaise_flop: [0.55, 5], foldToRaise_turn: [0.58, 5], foldToRaise_river: [0.62, 5],
  callBet_flop: [0.45, 6], callBet_turn: [0.42, 6], callBet_river: [0.4, 6],
  cont_small: [0.65, 6], cont_medium: [0.55, 6], cont_large: [0.42, 6],
  wtsd: [0.28, 6], wsd: [0.5, 6], bluffShown: [0.25, 4], weakCall: [0.3, 4],
};
export const STAT_KEYS = Object.keys(PRIORS);
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

const newPlayer = () => ({ hands: 0, aggr: 0, calls: 0, st: Object.fromEntries(STAT_KEYS.map((k) => [k, [0, 0]])) });
export const createModel = (n) => ({ players: Array.from({ length: n }, newPlayer), hand: null });

function add(m, id, key, hit, w = 1) {
  const s = m.players[id].st[key];
  const d = MODEL_CFG.decay;
  s[0] = s[0] * d + hit * w;
  s[1] = s[1] * d + w;
}

export function est(m, id, key) {
  const [h, n] = m.players[id].st[key];
  const [p, k] = PRIORS[key];
  return (h + p * k) / (n + k);
}

const sizeBucket = (ratio) => (ratio < 0.45 ? "small" : ratio < 0.8 ? "medium" : "large");

// How often a player's postflop aggression is a bluff (raises narrow their range less when high)
const bluffiness = (m, id) => clamp(est(m, id, "aggFreq") * 0.5 + est(m, id, "bluffShown") * 0.7, 0.1, 0.85);

export function modelStartHand(m, posById) {
  m.hand = {
    street: "preflop", opener: -1, pfa: -1, stealOpen: false, chain: true, bettor: {},
    p: Object.fromEntries(Object.entries(posById).map(([id, pos]) => [id, {
      pos, vpip: false, pfr: false, range: 1, folded: false, sawFlop: false, sd: false,
      firstIn: false, firstAct: false, faced1: false, faced3: false, riverAggr: false, calledRiver: false,
    }])),
  };
}

export function modelStreet(m, street, liveIds) {
  const h = m.hand;
  if (street !== "flop") h.chain = h.chain && h.bettor[street === "turn" ? "flop" : "turn"] === h.pfa;
  h.street = street;
  if (street === "flop") liveIds.forEach((id) => { if (h.p[id]) h.p[id].sawFlop = true; });
}

// Public preflop range estimate from the action just taken, using the player's own frequencies
function preflopRange(m, e) {
  const { id, pos, type, rc } = e;
  if (type === "raise") {
    if (rc === 0) {
      if (e.allIn) return clamp(Math.max(est(m, id, "jam"), est(m, id, "jamAny")) * 1.15 + 0.03, 0.06, 1);
      return clamp(est(m, id, "openRaise") * (POS_OPEN_MULT[pos] ?? 1), 0.05, 0.95);
    }
    // Frequent jammers get a range as wide as their observed jam rate
    const jamWide = e.allIn ? clamp(est(m, id, "jamAny") * 1.15, 0, 1) : 0;
    if (rc === 1) return Math.max(jamWide, clamp(est(m, id, "threeBet") * (e.allIn ? 1.6 : 1.15), 0.025, 0.45));
    return Math.max(jamWide * 0.8, clamp(est(m, id, "threeBet") * 0.45, 0.015, 0.2));
  }
  if (type !== "call") return 1;
  const vpip = est(m, id, "vpip");
  if (rc === 0) return clamp(vpip * 1.1, 0.15, 0.9);
  if (rc === 1) return clamp(vpip * (pos === "BB" ? 1.6 : pos === "SB" ? 1 : 0.85), 0.08, 0.8);
  return rc === 2 ? clamp(vpip * 0.35, 0.05, 0.3) : 0.06;
}

function preflopStats(m, e, h, hp) {
  const { id, pos, type, rc } = e;
  const w = e.nLive > 2 ? MULTIWAY_W : 1;
  if (!hp.firstAct && !(e.toCall === 0 && pos === "BB")) { hp.firstAct = true; add(m, id, "jamAny", type === "raise" && e.allIn ? 1 : 0); }
  if (rc === 0 && !hp.firstIn && !(pos === "BB" && e.toCall === 0)) {
    hp.firstIn = true;
    add(m, id, "openRaise", type === "raise" ? 1 : 0);
    add(m, id, "jam", type === "raise" && e.allIn ? 1 : 0);
    if (e.limpers === 0) add(m, id, "limp", type === "call" ? 1 : 0);
    if (e.limpers === 0 && STEAL_POS.has(pos)) add(m, id, "steal", type === "raise" ? 1 : 0);
  }
  if (rc === 1 && !hp.faced1) { hp.faced1 = true; add(m, id, "threeBet", type === "raise" ? 1 : 0, w); }
  if (rc === 1 && h.stealOpen && (pos === "SB" || pos === "BB")) add(m, id, "foldToSteal", type === "fold" ? 1 : 0);
  if (rc === 2 && id === h.opener && !hp.faced3) { hp.faced3 = true; add(m, id, "foldTo3bet", type === "fold" ? 1 : 0); }
  if (type === "raise") {
    if (rc === 0) { h.opener = id; h.stealOpen = e.limpers === 0 && STEAL_POS.has(pos); } else h.stealOpen = false;
    h.pfa = id; hp.vpip = true; hp.pfr = true;
  } else if (type === "call") {
    hp.vpip = true;
    if (rc >= 1) h.stealOpen = false;
  }
  hp.range = Math.min(hp.range, preflopRange(m, e));
}

function postflopStats(m, e, h, hp) {
  const { id, type, street } = e;
  const w = e.nLive > 2 ? MULTIWAY_W : 1;
  const P = m.players[id];
  const d = MODEL_CFG.decay;
  const fold = type === "fold" ? 1 : 0;
  if (e.toCall > 0) {
    const ratio = e.toCall / Math.max(1, e.potBefore - e.toCall);
    add(m, id, `cont_${sizeBucket(ratio)}`, 1 - fold, w);
    if (e.rc === 1) {
      add(m, id, `foldToBet_${street}`, fold, w);
      add(m, id, `callBet_${street}`, type === "call" ? 1 : 0, w);
      if (h.chain && h.bettor[street] === h.pfa) add(m, id, `foldToCbet_${street}`, fold, w);
    } else add(m, id, `foldToRaise_${street}`, fold, w);
  }
  if (street === "flop" && id === h.pfa && e.rc === 0) add(m, id, "cbet", type === "raise" ? 1 : 0, w);
  if (type !== "check") add(m, id, "aggFreq", type === "raise" ? 1 : 0, w);
  if (type === "raise") {
    P.aggr = P.aggr * d + 1; P.calls *= d;
    if (e.rc === 0) h.bettor[street] = id;
    if (street === "river") hp.riverAggr = true;
    const b = bluffiness(m, id);
    hp.range = Math.max(0.015, hp.range * (e.rc === 0 ? 0.5 + 0.4 * b : 0.35 + 0.45 * b));
  } else if (type === "call") {
    P.calls = P.calls * d + 1; P.aggr *= d;
    if (street === "river") hp.calledRiver = true;
    hp.range = Math.max(0.015, hp.range * clamp(0.5 + 0.45 * (1 - est(m, id, `foldToBet_${street}`)), 0.5, 0.95));
  }
}

/*
 * e = { id, pos, street, type: fold|check|call|raise, toCall, potBefore, rc (raises so far this street),
 *       limpers, nLive, allIn }   — all public.
 */
export function modelAction(m, e) {
  const h = m.hand;
  const hp = h?.p[e.id];
  if (!hp) return;
  if (e.type === "fold") hp.folded = true;
  if (e.street === "preflop") preflopStats(m, e, h, hp); else postflopStats(m, e, h, hp);
}

// shown = [{ id, score, won }] — only hands actually turned face up
export function modelShowdown(m, shown) {
  shown.forEach(({ id, score, won }) => {
    const hp = m.hand?.p[id];
    if (!hp) return;
    hp.sd = true;
    add(m, id, "wsd", won ? 1 : 0);
    const cat = handCategory(score);
    if (hp.riverAggr) add(m, id, "bluffShown", cat === 0 ? 1 : cat === 1 ? 0.35 : 0);
    if (hp.calledRiver) add(m, id, "weakCall", cat === 0 ? 1 : cat === 1 ? 0.4 : 0);
  });
}

export function modelEndHand(m) {
  if (!m.hand) return;
  Object.entries(m.hand.p).forEach(([id, hp]) => {
    m.players[id].hands++;
    add(m, id, "vpip", hp.vpip ? 1 : 0);
    add(m, id, "pfr", hp.pfr ? 1 : 0);
    if (hp.sawFlop) add(m, id, "wtsd", hp.sd ? 1 : 0);
  });
}

export const handRange = (m, id) => m.hand?.p[id]?.range ?? 1;

function styleTag(e, af) {
  if (e.vpip > 0.45 && e.callBet_flop > 0.55) return "station";
  if (e.vpip > 0.4 && (af > 2.5 || e.aggFreq > 0.5)) return "maniac";
  if (e.vpip < 0.17) return "nit";
  if (e.foldToBet_flop > 0.55 || e.foldToCbet_flop > 0.55) return "overfolder";
  if (af > 2.2 || e.pfr > 0.28) return "LAG";
  return "TAG";
}

// Smoothed snapshot of one player. conf ∈ [0,1) grows with hands observed.
export function profile(m, id) {
  const P = m.players[id];
  const e = Object.fromEntries(STAT_KEYS.map((k) => [k, est(m, id, k)]));
  const af = (P.aggr + 3) / (P.calls + 2);
  return { id, hands: P.hands, conf: P.hands / (P.hands + MODEL_CFG.confHands), est: e, af, style: styleTag(e, af) };
}

// Debug helper: rounded table of every player's current read
export function describeModels(m, names) {
  return m.players.map((_, id) => {
    const p = profile(m, id);
    const r = (x) => Math.round(x * 100);
    return {
      name: names[id], hands: p.hands, style: p.style, VPIP: r(p.est.vpip), PFR: r(p.est.pfr), "3B": r(p.est.threeBet),
      "F3B": r(p.est.foldTo3bet), steal: r(p.est.steal), FvSteal: r(p.est.foldToSteal), AF: p.af.toFixed(2),
      AFq: r(p.est.aggFreq), cbet: r(p.est.cbet), FvCbet: r(p.est.foldToCbet_flop), FvBetT: r(p.est.foldToBet_turn),
      FvBetR: r(p.est.foldToBet_river), contLarge: r(p.est.cont_large), WTSD: r(p.est.wtsd), bluffSD: r(p.est.bluffShown),
    };
  });
}
